import { useState, useEffect, useMemo } from "react";
import { Row, Col, Checkbox, Tag } from "antd";
import Select, { components, OptionProps } from "react-select";
import { PlanType } from "../types/planType";
import "./pagefilter.css";
import { useTranslation } from "react-i18next";

// Shared style overrides for react-select
const customStyles = {
  control: (provided: any) => ({
    ...provided,
    minWidth: "150px",
    width: "100%",
  }),
  container: (provided: any) => ({
    ...provided,
    width: "100%",
    minWidth: "150px",
    textAlign: "left",
  }),
  menu: (provided: any) => ({
    ...provided,
    backgroundColor: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: "4px",
    boxShadow: "none",
    marginTop: 0,
  }),
  menuList: (provided: any) => ({
    ...provided,
    padding: 8,
    maxHeight: 200,
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isFocused || state.isSelected ? "#F5F8FA" : "#fff",
    padding: "4px 8px",
    cursor: "pointer",
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: "#2D3748",
    fontSize: 14,
    fontWeight: 400,
  }),
};

// Remove default separator and hide values as tags inside the control
const baseSelectComponents = {
  IndicatorSeparator: () => null,
  ClearIndicator: () => null,
  MultiValue: () => null,
};

// Custom Option: render AntD Checkbox for each item
const CheckboxOption = (props: OptionProps<any, true>) => (
  <components.Option {...props}>
    <Checkbox
      checked={props.isSelected}
      onChange={() => null}
      style={{ marginRight: 8 }}
    >
      <span style={{ color: props.isSelected ? "#3182CE" : "inherit" }}>
        {props.label}
      </span>
    </Checkbox>
  </components.Option>
);

// Custom MenuList: append a Clear button styled like the panel footer
const CustomMenuList = (props: any) => {
  const { selectProps } = props;
  const { t } = useTranslation("cro");
  return (
    <>
      <components.MenuList {...props}>{props.children}</components.MenuList>
      {selectProps.onClear && (
        <div
          style={{
            borderTop: "1px solid #E2E8F0",
            borderRadius: "0 0 4px 4px",
            backgroundColor: "#fff",
            padding: "8px 12px",
            textAlign: "right",
            textTransform: "uppercase",
            fontSize: 12,
            cursor: "pointer",
            userSelect: "none",
            color: "#253f66",
            fontWeight: "bold",
          }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => selectProps.onClear()}
        >
          {t("Clear")}
        </div>
      )}
    </>
  );
};

interface PlansPageFiltersProps {
  plans: PlanType[];
  setPlans: React.Dispatch<React.SetStateAction<PlanType[]>>;
  commodityName: string;
}

// Filter & sort logic stays the same
const filterAndSortPlans = (
  base: PlanType[],
  terms: number[],
  offsets: string[],
  sorts: string[],
  commodity: string
) => {
  let out = [...base];
  if (sorts.includes("featured")) {
    out = [...base];
  }

  if (terms.length) out = out.filter((p) => terms.includes(p.contract_term));
  if (offsets.length) {
    if (commodity === "Gas") {
      out = out.filter(
        (p) =>
          p.commodity_clean_name === "Carbon Offset" &&
          offsets.includes(p.commodity_clean_per)
      );
    } else {
      out = out.filter((p) => offsets.includes(p.commodity_clean_name));
    }
  }
  if (sorts.includes("lowestPrice"))
    out.sort((a, b) => a.price2000 - b.price2000);
  if (sorts.includes("highestPrice"))
    out.sort((a, b) => b.price2000 - a.price2000);

  return out;
};

export default function PlansPageFilters({
  plans,
  setPlans,
  commodityName,
}: PlansPageFiltersProps) {
  const [basePlans] = useState<PlanType[]>(plans);
  const [selectedTerms, setSelectedTerms] = useState<number[]>([]);
  const [selectedOffsets, setSelectedOffsets] = useState<string[]>([]);
  const [selectedSorts, setSelectedSorts] = useState<string[]>([]);

  const { t } = useTranslation("cro");

  // Compute dropdown options
  const termOptions = useMemo(() => {
    let opts = [...basePlans];
    if (selectedOffsets.length) {
      if (commodityName === "Gas") {
        opts = opts.filter(
          (p) =>
            p.commodity_clean_name === "Carbon Offset" &&
            selectedOffsets.includes(p.commodity_clean_per)
        );
      } else {
        opts = opts.filter((p) =>
          selectedOffsets.includes(p.commodity_clean_name)
        );
      }
    }
    const unique = Array.from(new Set(opts.map((p) => p.contract_term))).sort(
      (a, b) => a - b
    );
    return unique.map((val) => ({
      value: val,
      label: `${val} ${t("months")}`,
    }));
  }, [basePlans, selectedOffsets, commodityName, t]);

  const offsetOptions = useMemo(() => {
    let opts = [...basePlans];
    if (selectedTerms.length)
      opts = opts.filter((p) => selectedTerms.includes(p.contract_term));
    if (commodityName === "Gas") {
      const perSet = Array.from(
        new Set(
          opts
            .filter((p) => p.commodity_clean_name === "Carbon Offset")
            .map((p) => p.commodity_clean_per)
        )
      ).sort((a, b) => Number(a) - Number(b));
      return perSet.map((val) => ({
        value: val,
        label: `${val}% Carbon Offset`,
      }));
    }
    const names = Array.from(
      new Set(opts.map((p) => p.commodity_clean_name))
    ).sort();
    return names.map((name) => ({ value: name, label: name }));
  }, [basePlans, selectedTerms, commodityName]);

  const sortOptions = [
    { value: "featured", label: t("Featured") },
    { value: "lowestPrice", label: t("Lowest Price") },
    { value: "highestPrice", label: t("Highest Price") },
  ];

  // Compute filtered plans
  const filtered = useMemo(
    () =>
      filterAndSortPlans(
        basePlans,
        selectedTerms,
        selectedOffsets,
        selectedSorts,
        commodityName
      ),
    [basePlans, selectedTerms, selectedOffsets, selectedSorts, commodityName]
  );

  useEffect(() => {
    setPlans(filtered);
  }, [filtered, setPlans]);

  return (
    <>
      <Row gutter={[8, 8]} align="middle" className="roboto-regular">
        <Col>
          <span style={{ color: "#4A5568" }} className="roboto-semibold">
            {t("Filter by")}
          </span>
        </Col>

        <Col xs={24} md={8} lg={4}>
          <Select
            isSearchable={false}
            isMulti
            options={termOptions}
            value={termOptions.filter((o) => selectedTerms.includes(o.value))}
            onChange={(opts) => setSelectedTerms(opts.map((o) => o.value))}
            placeholder={t("Term")}
            components={{
              ...baseSelectComponents,
              Option: CheckboxOption,
              MenuList: CustomMenuList,
            }}
            styles={customStyles}
            controlShouldRenderValue={false}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            onClear={() => setSelectedTerms([])}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <Select
            isSearchable={false}
            isMulti
            options={offsetOptions}
            value={offsetOptions.filter((o) =>
              selectedOffsets.includes(o.value)
            )}
            onChange={(opts) => setSelectedOffsets(opts.map((o) => o.value))}
            placeholder={
              commodityName === "Gas" ? t("CO₂ Offset %") : t("Energy Type")
            }
            components={{
              ...baseSelectComponents,
              Option: CheckboxOption,
              MenuList: CustomMenuList,
            }}
            styles={customStyles}
            controlShouldRenderValue={false}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            onClear={() => setSelectedOffsets([])}
          />
        </Col>

        <Col xs={12} md={6} lg={4} style={{ marginLeft: "auto" }}>
          <Select
            isSearchable={false}
            isMulti
            options={sortOptions}
            value={sortOptions.filter((o) => selectedSorts.includes(o.value))}
            onChange={(opts) => {
              const newSorts = opts.map((o) => o.value);
              setSelectedSorts([newSorts[newSorts.length - 1]]);
            }}
            placeholder={t("Sort By")}
            components={{
              ...baseSelectComponents,
              Option: CheckboxOption,
              MenuList: CustomMenuList,
            }}
            styles={customStyles}
            controlShouldRenderValue={false}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            onClear={() => setSelectedSorts([])}
          />
        </Col>
      </Row>
      {/* Tags row */}
      {(selectedTerms?.filter((x) => x)?.length ||
        selectedOffsets?.filter((x) => x)?.length ||
        selectedSorts?.filter((x) => x)?.length) > 0 ? (
        <Row
          align="middle"
          justify={"start"}
          style={{ textAlign: "left", marginTop: 16 }}
        >
          <Col>
            {selectedTerms.map((val) => {
              const opt = termOptions.find((o) => o.value === val);
              if (!opt) return null;
              return (
                <Tag
                  key={`term-${val}`}
                  style={{
                    backgroundColor: "#E2E8F0",
                    padding: ".25rem .5rem",
                    fontWeight: 500,
                    borderRadius: 4,
                    border: "none",
                  }}
                  closable
                  onClose={() =>
                    setSelectedTerms((prev) => prev.filter((v) => v !== val))
                  }
                >
                  {opt?.label}
                </Tag>
              );
            })}
            {selectedOffsets.map((val) => {
              const opt = offsetOptions.find((o) => o.value === val);
              if (!opt) return null;
              return (
                <Tag
                  key={`offset-${val}`}
                  style={{
                    backgroundColor: "#E2E8F0",
                    padding: ".25rem .5rem",
                    fontWeight: 500,
                    borderRadius: 4,
                    border: "none",
                  }}
                  closable
                  onClose={() =>
                    setSelectedOffsets((prev) => prev.filter((v) => v !== val))
                  }
                >
                  {opt?.label}
                </Tag>
              );
            })}
            {selectedSorts.map((val) => {
              const opt = sortOptions.find((o) => o.value === val);
              if (!opt) return null;
              return (
                <Tag
                  key={`sort-${val}`}
                  style={{
                    backgroundColor: "#E2E8F0",
                    padding: ".25rem .5rem",
                    fontWeight: 500,
                    borderRadius: 4,
                    border: "none",
                  }}
                  closable
                  onClose={() =>
                    setSelectedSorts((prev) => prev.filter((v) => v !== val))
                  }
                >
                  {opt?.label}
                </Tag>
              );
            })}
            <span
              style={{
                cursor: "pointer",
                textTransform: "uppercase",
                color: "#253f66",
                whiteSpace: "nowrap",
                textAlign: "left",
              }}
              onClick={() => {
                setSelectedTerms([]);
                setSelectedSorts([]);
                setSelectedOffsets([]);
              }}
            >
              {t("Clear All")}
            </span>
          </Col>
        </Row>
      ) : null}
    </>
  );
}
